/**
 * Help System — abstract base
 *
 * Holds all the behaviour shared by the two help systems (reactive open/close
 * state, context resolution from the current route, content loading, related
 * topics, and back-history) while keeping them as two DISTINCT injectable
 * systems. Each concrete system supplies only its identity, its route-map and
 * its locale — its content lives in a separate `assets/help/<id>/` tree.
 *
 * This is deliberately framework-faithful to the Bug Tracker `useHelp`
 * composable, re-expressed as an Angular service with RxJS state.
 */

import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { HelpSystemId, RelatedTopic, RouteHelpMapping } from './help.types';
import { loadHelpContent } from './help-content.loader';
import { parseHelpDoc, renderMarkdown } from './markdown-renderer';

export abstract class HelpSystemBase {
  abstract readonly systemId: HelpSystemId;
  protected abstract readonly routeMap: RouteHelpMapping[];
  protected locale = 'en';

  readonly isOpen$ = new BehaviorSubject<boolean>(false);
  readonly isLoading$ = new BehaviorSubject<boolean>(false);
  readonly title$ = new BehaviorSubject<string>('');
  readonly html$ = new BehaviorSubject<string>('');
  readonly relatedTopics$ = new BehaviorSubject<RelatedTopic[]>([]);
  readonly canGoBack$ = new BehaviorSubject<boolean>(false);

  private currentTopic = '';
  private history: string[] = [];
  private destroy$ = new Subject<void>();

  constructor(protected router: Router) {
    // Keep context-help in sync with navigation while the drawer is open.
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isOpen$.value) {
          const topic = this.contextTopic();
          if (topic !== this.currentTopic) this.loadTopic(topic);
        }
      });
  }

  /** Resolve the help topic for the current URL via this system's route-map. */
  contextTopic(): string {
    const url = this.router.url.split('?')[0];
    const [path, hash] = url.split('#');
    return this.topicForRoute(path, hash);
  }

  /** Open the drawer on the context-appropriate article. */
  open(): void {
    this.isOpen$.next(true);
    const topic = this.contextTopic();
    if (topic !== this.currentTopic || !this.html$.value) this.loadTopic(topic);
  }

  /** Open the drawer directly on a specific topic. */
  openTo(topic: string): void {
    this.isOpen$.next(true);
    this.loadTopic(topic);
  }

  close(): void { this.isOpen$.next(false); }

  toggle(): void { this.isOpen$.value ? this.close() : this.open(); }

  /** Navigate to another topic, pushing the current one onto history. */
  navigateTo(topic: string): void {
    if (this.currentTopic) {
      this.history.push(this.currentTopic);
      this.canGoBack$.next(true);
    }
    this.loadTopic(topic);
  }

  goBack(): void {
    const prev = this.history.pop();
    this.canGoBack$.next(this.history.length > 0);
    if (prev) this.loadTopic(prev);
  }

  goToIndex(): void { this.navigateTo('getting-started'); }

  setLocale(locale: string): void {
    if (locale === this.locale) return;
    this.locale = locale;
    if (this.isOpen$.value && this.currentTopic) this.loadTopic(this.currentTopic);
  }

  destroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  private async loadTopic(topic: string): Promise<void> {
    this.isLoading$.next(true);
    try {
      const raw = await loadHelpContent(this.systemId, topic, this.locale);
      const doc = parseHelpDoc(raw);
      this.currentTopic = topic;
      this.title$.next(doc.title || titleFromTopic(topic));
      this.html$.next(renderMarkdown(doc.body));
      this.relatedTopics$.next(
        doc.relatedTopics.map(t => ({ topic: t, title: titleFromTopic(t) })),
      );
    } finally {
      this.isLoading$.next(false);
    }
  }

  private topicForRoute(path: string, hash?: string): string {
    const normalized = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

    // 1) exact match including hash
    if (hash) {
      const withHash = this.routeMap.find(m => m.route === `${normalized}#${hash}`);
      if (withHash) return withHash.topic;
    }
    // 2) exact path match
    const exact = this.routeMap.find(m => m.route === normalized);
    if (exact) return exact.topic;

    // 3) parameterised match (`:id` segments)
    const segments = normalized.split('/').filter(Boolean);
    for (const m of this.routeMap) {
      if (m.route === '*' || m.route.includes('#')) continue;
      const routeSegments = m.route.split('/').filter(Boolean);
      if (routeSegments.length !== segments.length) continue;
      const matches = routeSegments.every(
        (rs, idx) => rs.startsWith(':') || rs === segments[idx],
      );
      if (matches) return m.topic;
    }

    // 4) fallback
    const fallback = this.routeMap.find(m => m.route === '*');
    return fallback ? fallback.topic : 'getting-started';
  }
}

function titleFromTopic(topic: string): string {
  const last = topic.split('/').pop() || topic;
  return last.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
